import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Info, Loader2 } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Skeleton } from "../../components/ui/skeleton";
import LogoUploadField from "../../components/LogoUploadField";
import { options } from "../../constant";
import { API } from "../../constant/api";
import { businessDetailsEditSchema } from "../../utils/authSchemas";
import { useBusiness } from "../../hooks/use-business";
import { useBusinessImageUpload } from "../../hooks/use-business-image-upload";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import ReadOnlyField from "./components/ReadOnlyField";
import SettingsSection from "./components/SettingsSection";

const SIZES = ["1-10", "11-50", "51-100", "101 +"];

/** Only what PATCH /api/businesses/:id actually accepts under `details`. */
type BusinessValues = {
  address: string;
  size: string;
  description: string;
};

const BusinessDetails = () => {
  const axiosInstance = useAuthAxios();
  const queryClient = useQueryClient();
  const { business, businessId, isRegistered, isLoading } = useBusiness();

  const form = useForm<BusinessValues>({
    resolver: yupResolver(businessDetailsEditSchema) as any,
    defaultValues: {
      address: "",
      size: "",
      description: "",
    },
    mode: "onTouched",
  });

  const { reset } = form;

  const update = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      const { data } = await axiosInstance.patch(
        API.updateBusiness(businessId!),
        payload
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Business details updated");
      queryClient.invalidateQueries({ queryKey: ["get-business"] });
      queryClient.invalidateQueries({ queryKey: ["get-businesses"] });
      reset(form.getValues());
    },
    onError: (e: any) =>
      toast.error(errorMessage(e?.response?.data?.message || e?.message)),
  });

  const logo = useBusinessImageUpload({ documentType: "LOGO" });

  const { sync: syncLogo } = logo;

  // Populate once the business request resolves.
  useEffect(() => {
    if (!business) return;
    reset({
      address: business.details?.address ?? "",
      size: business.details?.size ?? "",
      description: business.details?.description ?? "",
    });
    syncLogo(business.logo);
  }, [business, reset, syncLogo]);

  const onSubmit = (values: BusinessValues) => {
    if (!businessId || update.isPending) return;
    if (logo.isUploading) {
      toast.error("Hold on — your logo is still uploading");
      return;
    }
    update.mutate({
      details: {
        address: values.address,
        size: values.size,
        description: values.description,
      },
      ...(logo.isDirty && { logo: logo.objectURL }),
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-[420px] w-full rounded-xl" />
        <Skeleton className="h-[200px] w-full rounded-xl" />
      </div>
    );
  }

  // Industry is stored as a slug ("beauty_and_wellness"); show its label.
  const industryLabel =
    options.find(
      (option: { value: string }) =>
        option.value === business?.details?.industry
    )?.label ?? business?.details?.industry;

  /**
   * There is no verified/unverified split on this tab. Logo, size, address and
   * description are operational details a business changes as it grows — an
   * office move doesn't invalidate a KYC check — so they stay editable for
   * every business, registered or not. What is fixed is fixed for everyone:
   * name, email, industry and country are set at creation and the PATCH
   * endpoint doesn't accept them. (Registration *documents* are a different
   * matter and do still lock — see the Documents tab.)
   */
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <SettingsSection
          title="Business details"
          description="These appear on your payment pages and customer receipts."
          action={
            <Button
              type="submit"
              disabled={
                (!form.formState.isDirty && !logo.isDirty) ||
                update.isPending ||
                logo.isUploading
              }
              className="h-10 rounded-xl px-5 text-[14px] font-semibold"
            >
              {update.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Save changes"
              )}
            </Button>
          }
        >
          <div className="mb-6">
            <LogoUploadField
              name={business?.name}
              imageURL={logo.displayURL}
              status={logo.status}
              error={logo.error}
              onSelect={logo.upload}
              onRetry={logo.retry}
              onTooLarge={(message) => toast.error(message)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 mds:grid-cols-2">
            {/* Set at creation and not accepted by the PATCH — display only. */}
            <ReadOnlyField label="Business name" value={business?.name} />
            <ReadOnlyField label="Business email" value={business?.email} />
            <ReadOnlyField label="Industry" value={industryLabel} />
            <ReadOnlyField label="Country" value={business?.details?.country} />

            <FormField
              control={form.control}
              name="size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business size</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a size" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {SIZES.map((size) => (
                        <SelectItem key={size} value={size}>
                          {size} employees
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="mds:col-span-2">
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter full address of business"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mds:col-span-2">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe your business. What is it about? How long has it been in operation?"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="mt-5 flex items-start gap-3 rounded-xl border bg-muted/50 p-4">
            <Info className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <p className="text-[13px] font-medium text-muted-foreground">
              Your logo, address, size and description are yours to change.
              Everything else was set when the business was created — contact
              support if it needs to be corrected.
            </p>
          </div>
        </SettingsSection>

        <SettingsSection
          title="Registration"
          description="Managed from the Documents tab."
        >
          <div className="grid grid-cols-1 gap-4 mds:grid-cols-2">
            <ReadOnlyField
              label="Business type"
              value={isRegistered ? "Registered business" : "Starter business"}
            />
            <ReadOnlyField label="RN / BN number" value={business?.bnNumber} />
          </div>
        </SettingsSection>
      </form>
    </Form>
  );
};

export default BusinessDetails;
